import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { BASE_URL, STATIC_PAGES } from "../seo/pages";
import { PublicHeader } from "../components/PublicHeader";
import { authFetch, getApiBase, getPathPrefix, resolveMediaUrl } from "../../utils/preview";
import "../styles/PublicCommon.css";
import "./NewsPage.css";

// お知らせデータの型定義
interface Announce {
  id: string;
  title: string;
  content: string;
  thumbnail_image: {
    storage_path: string;
  } | null;
  publish_date: string;
  display_priority: number;
  is_published: boolean;
}

// 日付をフォーマットする関数
const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
};

// メディアURLを生成（/static/ も R2 も透過的に扱う）
const getMediaUrl = (storagePath: string): string => resolveMediaUrl(storagePath);

const pathPrefix = getPathPrefix();

export const NewsPage = () => {
  const [announces, setAnnounces] = useState<Announce[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // APIからお知らせデータを取得
  useEffect(() => {
    const fetchAnnounces = async () => {
      try {
        setLoading(true);
        const response = await authFetch(`${getApiBase()}/announces`);
        if (response.ok) {
          const data = await response.json();
          setAnnounces(data.announces || []);
          setError(null);
        } else {
          throw new Error("データの取得に失敗しました");
        }
      } catch (err) {
        console.error("お知らせの取得に失敗しました:", err);
        setError(err instanceof Error ? err.message : "エラーが発生しました");
      } finally {
        setLoading(false);
      }
    };
    fetchAnnounces();
  }, []);

  // 構造化データを生成
  const structuredData = useMemo(() => {
    // 記事一覧の構造化データ（ItemList）
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "お知らせ一覧",
      description: "都建設株式会社からのお知らせ一覧",
      numberOfItems: announces.length,
      itemListElement: announces.map((news, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "NewsArticle",
          "@id": `${BASE_URL}/news/${news.id}`,
          headline: news.title,
          datePublished: news.publish_date,
          dateModified: news.publish_date,
          description:
            news.content.length > 100
              ? news.content.substring(0, 100) + "..."
              : news.content,
          author: {
            "@type": "Organization",
            name: "都建設株式会社",
            url: BASE_URL,
          },
          publisher: {
            "@type": "Organization",
            name: "都建設株式会社",
            logo: {
              "@type": "ImageObject",
              url: `${BASE_URL}/images/logo.png`,
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${BASE_URL}/news/${news.id}`,
          },
        },
      })),
    };

    // パンくずリストの構造化データ
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "ホーム",
          item: BASE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "お知らせ",
          item: `${BASE_URL}/news`,
        },
      ],
    };

    return [itemListSchema, breadcrumbSchema];
  }, [announces]);

  return (
    <>
      <SEOHead
        page={STATIC_PAGES.news}
        ogType="website"
        structuredData={structuredData}
      />

      <div className="public-page">
        <PublicHeader activePage="news" />

        {/* メインコンテンツ */}
        <main id="main-content" role="main">
          {/* ページヘッダー */}
          <section className="page-header">
            <div className="page-header-overlay" />
            <div className="page-header-content">
              <h1 className="page-title">お知らせ</h1>
              <p className="page-subtitle">News</p>
            </div>
          </section>

          {/* お知らせ一覧セクション */}
          <section className="news-list-section">
            <div className="container">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>読み込み中...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>{error}</p>
                </div>
              ) : announces.length === 0 ? (
                <div className="empty-state">
                  <p>お知らせはありません</p>
                </div>
              ) : (
                <div className="news-items">
                  {announces.map((news) => {
                    const excerpt =
                      news.content.length > 100
                        ? news.content.substring(0, 100) + "..."
                        : news.content;

                    return (
                      <article key={news.id} className="news-item-card">
                        <Link to={`${pathPrefix}/news/${news.id}`} className="news-item-link">
                          {news.thumbnail_image?.storage_path && (
                            <div className="news-item-image">
                              <img
                                src={getMediaUrl(news.thumbnail_image.storage_path)}
                                alt={news.title}
                              />
                            </div>
                          )}
                          <div className="news-item-content">
                            <div className="news-item-header">
                              <time
                                className="news-item-date"
                                dateTime={news.publish_date}
                              >
                                {formatDate(news.publish_date)}
                              </time>
                            </div>
                            <h2 className="news-item-title">{news.title}</h2>
                            <p className="news-item-excerpt">{excerpt}</p>
                            <span className="news-item-read-more">
                              続きを読む &rarr;
                            </span>
                          </div>
                        </Link>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </main>

        {/* フッター */}
        <footer className="public-footer" role="contentinfo">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/static/miyako-logo-small.png"
                srcSet="/static/miyako-logo-small.png 1x, /static/miyako-logo-2x.png 2x"
                alt="都建設株式会社"
                className="footer-logo-image"
                width={60}
                height={45}
              />
              <span className="footer-company-name">都建設株式会社</span>
            </div>

            <address className="footer-address-block">
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432"
                className="footer-address"
                target="_blank"
                rel="noopener noreferrer"
              >
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432
              </a>

              <a href="tel:0894-62-4511" className="footer-phone">
                お電話：<span itemProp="telephone">0894-62-4511</span>
              </a>
            </address>

            <nav className="footer-nav" aria-label="フッターナビゲーション">
              <Link to="/">ホーム</Link>
              <Link to="/about">会社概要</Link>
              <Link to="/services">業務内容</Link>
              <Link to="/works">施工実績</Link>
              <Link to="/recruit">採用情報</Link>
              <Link to="/contact">お問い合わせ</Link>
            </nav>

            <p className="footer-copyright">
              <small>&copy;2026 都建設株式会社</small>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default NewsPage;
